import { SvgIcon } from '@/components/icon';
import { Promotion } from '@/types';
import {
  Typography,
  Space,
  Input,
  Select,
  Pagination,
  Table,
  Row,
  Col,
  DatePicker,
} from 'antd';
import { set } from 'date-fns';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import NoData from 'src/assets/images/no-data.png';

const { Option } = Select;
const { Text } = Typography;

const { RangePicker } = DatePicker;

interface Props {
  data: (Promotion & { key: string })[];
  columns: any[];
  promotionFilters: any;
  setPromotionFilters: Dispatch<SetStateAction<any>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  limit: number;
  setLimit: React.Dispatch<React.SetStateAction<number>>;
  total: number;
}

export default function PromotionsTable({
  data,
  columns,
  promotionFilters,
  setPromotionFilters,
  total,
  page,
  setPage,
  limit,
  setLimit,
}: Props) {
  const [filteredData, setFilteredData] = useState(data);
  // State for search input
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // Search Handler
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchValue(value);
    setFilteredData(
      data.filter((item) =>
        Object.values(item).some((field) =>
          String(field).toLowerCase().includes(value),
        ),
      ),
    );
    setPage(1); // Reset pagination on new search
  };

  const getCustomLabel = (size: number) => {
    const start = (page - 1) * size + 1;
    const end = Math.min(page * size, filteredData.length);
    return `${start}-${end} of ${total}`;
  };

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        'selectedRows: ',
        selectedRows,
      );
    },
    getCheckboxProps: (record: any) => ({
      disabled: record.name === 'Disabled User', // Column configuration not to be checked
      name: record.name,
    }),
  };

  return (
    <>
      <div className="tables-info">
        <Text style={{ color: '#9FA1AD', fontWeight: 500 }}>
          {total} Total Promotions
        </Text>
        <span className="tables-divider" />
        <Text style={{ color: '#9FA1AD', fontWeight: 500 }}>
          Updated 20 min ago
        </Text>
      </div>
      <div className="tables-wrapper">
        {/* Actions Section (Search and Pagination Controls) */}
        <div
          className="tables-actions"
          style={{
            margin: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <Space
            direction="horizontal"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div className="tables-actions-search">
              {/* Naming */}

              <Typography.Title
                level={3}
                style={{ textWrap: 'nowrap', marginBlock: 16 }}
              >
                Promotions
              </Typography.Title>
              {/* Search Bar */}
              <Input.Search
                placeholder="Search"
                allowClear
                enterButton={
                  <SvgIcon
                    type={'search'}
                    height={12}
                    width={12}
                    style={{ stroke: 'white' }}
                  />
                }
                style={{ maxWidth: '300px' }}
                value={searchValue}
                onChange={handleSearch}
              />
            </div>
            <div className="tables-actions-pagination">
              {/* Pagination Settings */}
              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <div className="table-selector">
                  <Select
                    className="table-selector-select"
                    style={{ width: 120 }}
                    value={limit}
                    size="small"
                    onChange={(size) => {
                      setLimit(size);
                      setPage(1);
                    }}
                    optionLabelProp="label"
                  >
                    <Option value={5} label={getCustomLabel(5)}>
                      5 per page
                    </Option>
                    <Option value={10} label={getCustomLabel(10)}>
                      10 per page
                    </Option>
                    <Option value={20} label={getCustomLabel(20)}>
                      20 per page
                    </Option>
                  </Select>
                </div>
              </div>
              {/* Pagination (Moved Above) */}
              <Pagination
                current={page}
                pageSize={limit}
                total={total}
                onChange={(page) => setPage(page)}
                showSizeChanger={false}
                className="tables-actions-pagination"
                style={{ marginBottom: '0' }}
              />
            </div>
          </Space>
          <Space>
            <Row justify={'space-between'} align={'middle'} style={{ gap: 8 }}>
              <Col>
                <Select
                  defaultValue="All"
                  optionLabelProp="label"
                  onChange={(status) =>
                    setPromotionFilters({ ...promotionFilters, status })
                  }
                >
                  <Option value="All" label={'Status: All'}>
                    All
                  </Option>
                  <Option value="Running" label={'Status: Running'}>
                    Running
                  </Option>
                  <Option value="Paused" label={'Status: Paused'}>
                    Paused
                  </Option>
                </Select>
              </Col>

              <Col>
                <Select
                  defaultValue="All"
                  optionLabelProp="label"
                  onChange={(effect) =>
                    setPromotionFilters({ ...promotionFilters, effect })
                  }
                >
                  <Option value="All" label={'Effect: All'}>
                    All
                  </Option>
                  <Option value="Price" label={'Effect: Price'}>
                    Price
                  </Option>
                  <Option value="Gift" label={'Effect: Gift'}>
                    Gift
                  </Option>
                </Select>
              </Col>
              <Col>
                <Select
                  defaultValue="All"
                  optionLabelProp="label"
                  onChange={(affected) =>
                    setPromotionFilters({ ...promotionFilters, affected })
                  }
                >
                  <Option value="All" label={'Affected: All'}>
                    All
                  </Option>
                  <Option value="Franchise" label={'Affected: Franchise'}>
                    Franchise
                  </Option>
                  <Option value="Consultant" label={'Affected: Consultant'}>
                    Consultant
                  </Option>
                </Select>
              </Col>

              <Col>
                <RangePicker
                  defaultValue={[null, null]}
                  onChange={(dates) =>
                    setPromotionFilters({
                      ...promotionFilters,
                      startDate: dates?.[0] || null,
                      endDate: dates?.[1] || null,
                    })
                  }
                />
              </Col>
            </Row>
          </Space>
        </div>

        {filteredData.length === 0 ? (
          <div className="no-data-wrapper">
            <img src={NoData} alt="No Data" />
            <Typography.Text
              style={{
                color: '#9FA1AD',
                fontWeight: 500,
                maxWidth: '200px',
                textAlign: 'center',
              }}
            >
              {`It looks like there aren't any promotions.`}
            </Typography.Text>
          </div>
        ) : (
          <Table
            rowSelection={rowSelection}
            dataSource={filteredData}
            columns={columns}
            pagination={false}
            style={{ marginTop: '0' }}
          />
        )}
      </div>
    </>
  );
}
